import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ArrowLeft, ArrowUpRight, Calendar } from 'lucide-react';
import { PageShell } from '@/components/brand/PageShell';
import { LoadingState, EmptyState } from '@/components/brand/States';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return ''; }
};

const RegistrySearch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (initialQuery) handleSearch(initialQuery);
    // eslint-disable-line
  }, []); // eslint-disable-line

  const handleSearch = async (query = searchQuery) => {
    if (!query.trim()) return;
    setLoading(true); setSearched(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/registries/search?query=${encodeURIComponent(query.trim())}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const data = await response.json();
        setResults(data.registries || []);
      } else {
        setResults([]);
      }
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <div className="container-page py-12 sm:py-16 max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-clay-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="mt-10 text-center">
          <LogoMark size={28} className="text-clay-700 mx-auto mb-5" />
          <div className="eyebrow mb-3">Find someone's registry</div>
          <h1 className="font-display text-display-xl text-ink leading-[1.05]">
            Looking for a basket?
          </h1>
          <p className="mt-4 text-lg text-ink-soft max-w-md mx-auto">
            Search by the parent's name, or the registry name they've chosen.
          </p>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
          className="mt-10"
        >
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-muted" />
            <input
              type="text"
              placeholder="A name, a couple, a registry"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-14 pl-14 pr-32 text-[15px] rounded-full bg-cream-50 border border-border placeholder:text-ink-muted text-ink focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300 shadow-soft-1"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <BrandButton
                type="submit"
                disabled={loading}
                loading={loading}
                size="md"
              >
                Search
              </BrandButton>
            </div>
          </div>
        </form>

        {searched && (
          <div className="mt-12">
            {loading ? (
              <LoadingState label="Looking through baskets…" compact />
            ) : results.length === 0 ? (
              <EmptyState
                eyebrow="No matches"
                title="We couldn't find that one."
                body={`Nothing matched "${searchQuery}". Try a different name, or ask the person for their direct link.`}
              />
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-ink-muted px-1">
                  {results.length} {results.length === 1 ? 'match' : 'matches'}
                </p>
                {results.map((registry) => (
                  <button
                    key={registry.id}
                    onClick={() => navigate(`/registry/${registry.id}`)}
                    className="group w-full text-left rounded-2xl bg-cream-50 border border-border/70 p-5 sm:p-6 hover:border-clay-200 hover:shadow-soft-2 transition-all"
                    data-testid={`registry-result-${registry.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="eyebrow mb-2 text-clay-700">
                          {registry.event_type || 'Registry'}
                        </div>
                        <h3 className="font-display text-xl text-ink leading-snug">
                          {registry.registry_name || `${registry.parent_name || 'Their'} basket`}
                        </h3>
                        {registry.parent_name && (
                          <p className="mt-1 text-sm text-ink-soft">by {registry.parent_name}</p>
                        )}
                        {(registry.due_date || registry.event_date) && (
                          <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-ink-muted">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(registry.due_date || registry.event_date)}
                          </p>
                        )}
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-ink-muted group-hover:text-clay-700 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default RegistrySearch;
